/**
 * WorkshopDetails Component - Displays selected workshop details and management
 */
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Edit, Trash2, FileText, CheckCircle, Send, XCircle, PlayCircle, AlertTriangle, RefreshCw } from 'lucide-react';
import { getStatusBadge } from './WorkshopUtils';
import { ContentManager } from './ContentManager';
import { CollaboratorPanel } from './CollaboratorPanel';

export const WorkshopDetails = ({
  workshop,
  user,
  canEdit,
  isCreator,
  myCollab,
  onEdit,
  onDelete,
  onSubmitForApproval,
  onApproveFinal,
  onInviteCollaborator,
  onDeactivate,
  onReactivate,
  // Content management callbacks
  onAddModule,
  onEditModule,
  onDeleteModule,
  onAddFolder,
  onEditFolder,
  onDeleteFolder,
  onAddContent,
  onEditContent,
  onDeleteContent
}) => {
  if (!workshop) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <FileText className="w-16 h-16 mx-auto mb-4 text-gray-600" />
          <p className="text-gray-400">Select a workshop to view details and edit content</p>
        </CardContent>
      </Card>
    );
  }
  
  const isActive = ['approved', 'live', 'published'].includes(workshop.status);
  const isInactive = workshop.status === 'inactive';
  const isPendingApproval = workshop.status === 'pending_admin_approval';
  const isModified = workshop.modified_since_approval && isActive;
  
  return (
    <Card className="bg-slate-800">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-3 text-white">
              {workshop.title}
              {getStatusBadge(workshop.status)}
              {isModified && (
                <Badge className="bg-amber-500 text-white animate-pulse">Modified</Badge>
              )}
            </CardTitle>
            <CardDescription className="mt-1 text-gray-300">
              ${workshop.price.toFixed(2)} (Users pay: ${workshop.total_price.toFixed(2)})
            </CardDescription>
          </div>
          <div className="flex gap-2">
            {canEdit && !isPendingApproval && (
              <Button size="sm" variant="outline" onClick={onEdit}>
                <Edit className="w-4 h-4 mr-1" /> Edit
              </Button>
            )}
            
            {/* Deactivate button for active workshops */}
            {isActive && isCreator && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="outline" className="text-orange-400 border-orange-400 hover:bg-orange-400/10">
                    <XCircle className="w-4 h-4 mr-1" /> Deactivate
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Deactivate Workshop?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will remove the workshop from sale. Existing purchasers will retain access to the content.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={() => onDeactivate(workshop.id)}
                      className="bg-orange-600 hover:bg-orange-700"
                    >
                      Deactivate
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            
            {/* Reactivate button for inactive workshops */}
            {isInactive && isCreator && (
              <Button 
                size="sm" 
                className="bg-green-600 hover:bg-green-700"
                onClick={() => onReactivate(workshop.id)}
              >
                <PlayCircle className="w-4 h-4 mr-1" /> Reactivate
              </Button>
            )}
            
            {workshop.status === 'draft' && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="destructive">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Workshop?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently delete this workshop and all its content.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => onDelete(workshop.id)}>
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
        
        {/* Inactive status notice */}
        {isInactive && (
          <div className="mt-3 p-3 bg-orange-500/10 border border-orange-500 rounded-lg">
            <div className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-orange-400" />
              <span className="font-medium text-orange-400">Workshop is Inactive</span>
            </div>
            <p className="text-sm text-gray-300 mt-1">
              This workshop is not visible for sale. Existing purchasers still have access.
            </p>
          </div>
        )}
        
        {/* Modified since approval notice */}
        {isModified && (
          <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                <span className="font-medium text-amber-400">Workshop Modified</span>
              </div>
              <Button 
                size="sm" 
                className="bg-amber-600 hover:bg-amber-700"
                onClick={() => onSubmitForApproval(workshop.id)}
              >
                <RefreshCw className="w-4 h-4 mr-1" /> Resubmit for Approval
              </Button>
            </div>
            <p className="text-sm text-gray-300 mt-1">
              You&apos;ve made changes since this workshop was approved. Changes are visible to existing purchasers, 
              but please resubmit for admin review to ensure compliance.
            </p>
          </div>
        )}
        
        {/* Pending re-approval notice */}
        {isPendingApproval && (
          <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-500 rounded-lg">
            <div className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-yellow-400" />
              <span className="font-medium text-yellow-400">Pending Admin Approval</span>
            </div>
            <p className="text-sm text-gray-300 mt-1">
              Your workshop is being reviewed by an admin. Editing is disabled until approval.
            </p>
          </div>
        )}
      </CardHeader>
      
      <CardContent>
        <Tabs defaultValue="content" className="w-full">
          <TabsList className="grid grid-cols-3 mb-4">
            <TabsTrigger value="content">Content</TabsTrigger>
            <TabsTrigger value="collaborators">Collaborators</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>
          
          <TabsContent value="content" className="space-y-4">
            <ContentManager
              workshop={workshop}
              canEdit={canEdit}
              onAddModule={onAddModule}
              onEditModule={onEditModule}
              onDeleteModule={onDeleteModule}
              onAddFolder={onAddFolder}
              onEditFolder={onEditFolder}
              onDeleteFolder={onDeleteFolder}
              onAddContent={onAddContent}
              onEditContent={onEditContent}
              onDeleteContent={onDeleteContent}
            />
            
            {/* Submit for approval - single collaborator */}
            {workshop.status === 'draft' && workshop.modules?.length > 0 && workshop.collaborators?.length === 1 && (
              <SubmitForApprovalSection
                workshopId={workshop.id}
                onSubmit={onSubmitForApproval}
              />
            )}
            
            {/* Rejection reason */}
            {workshop.status === 'rejected' && workshop.rejection_reason && (
              <div className="mt-4 p-4 bg-red-500/10 border border-red-500 rounded-lg">
                <h4 className="font-medium text-red-400 mb-2">Rejection Reason</h4>
                <p className="text-sm">{workshop.rejection_reason}</p>
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="collaborators">
            <CollaboratorPanel
              workshop={workshop}
              isCreator={isCreator}
              canEdit={canEdit}
              myCollab={myCollab}
              onInviteCollaborator={onInviteCollaborator}
              onApproveFinal={onApproveFinal}
              onSubmitForApproval={onSubmitForApproval}
            />
          </TabsContent>
          
          <TabsContent value="settings">
            <SettingsPanel workshop={workshop} />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

const SubmitForApprovalSection = ({ workshopId, onSubmit }) => (
  <div className="mt-6 p-4 bg-cyan-600/10 border border-cyan-500 rounded-lg">
    <h4 className="font-medium mb-2">Ready to submit?</h4>
    <p className="text-sm text-gray-400 mb-3">
      Once submitted, your workshop will be reviewed by an admin before going live.
    </p>
    <Button 
      onClick={() => onSubmit(workshopId)} 
      className="bg-green-600 hover:bg-green-700"
    >
      <Send className="w-4 h-4 mr-2" /> Submit for Admin Approval
    </Button>
  </div>
);

const SettingsPanel = ({ workshop }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div className="p-4 bg-slate-800 rounded-lg">
        <Label className="text-gray-400 text-sm">Category</Label>
        <p className="font-medium mt-1">
          {workshop.category?.name || 'None'}
        </p>
      </div>
      <div className="p-4 bg-slate-800 rounded-lg">
        <Label className="text-gray-400 text-sm">Access Type</Label>
        <p className="font-medium mt-1 capitalize">
          {workshop.access_type === 'lifetime' 
            ? 'Lifetime Access' 
            : `${workshop.access_duration_days} Days`
          }
        </p>
      </div>
    </div>
    
    <div className="p-4 bg-slate-800 rounded-lg">
      <Label className="text-gray-400 text-sm">Description</Label>
      <p className="mt-1 whitespace-pre-wrap">{workshop.description}</p>
    </div>
    
    {workshop.status === 'live' && (
      <div className="p-4 bg-green-500/10 border border-green-500 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle className="w-5 h-5 text-green-500" />
          <span className="font-medium text-green-400">Workshop is Live!</span>
        </div>
        <p className="text-sm text-gray-400">
          Your workshop is visible to all users in the Workshops section.
        </p>
      </div>
    )}
  </div>
);
